/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, TextField, Typography } from '@mui/material';
import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from './Components/Accordion';
import { ToolbarButton } from './Components/ToolbarButton';
/**
 * Toolbar for the drag and drop component editor.
 */
export const WidgetEditorToolbar = ({ widgets, groupLabelProvider, widgetLabelProvider }) => {
    const groups = splitWidgetsIntoGroups(widgets);
    const [expandedGroupName, setExpandedGroupName] = React.useState(groups.length > 0 ? groups[0].name : '');
    const handleExpandedGroupChange = (groupName) => (event, isExpanded) => {
        setExpandedGroupName(isExpanded ? groupName : '');
    };
    const searchInputRef = React.useRef(undefined);
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        var _a, _b;
        if (expandedGroupName === searchAllComponentsGroupName) {
            (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = searchInputRef.current) === null || _b === void 0 ? void 0 : _b.select();
        }
    }, [expandedGroupName]);
    React.useEffect(() => {
        const listener = (event) => {
            if (((event.ctrlKey || event.metaKey) && event.key === 'e') || event.key === 'F2') {
                setExpandedGroupName((prevGroupName) => {
                    var _a, _b;
                    if (prevGroupName === searchAllComponentsGroupName) {
                        (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        (_b = searchInputRef.current) === null || _b === void 0 ? void 0 : _b.select();
                    }
                    return searchAllComponentsGroupName;
                });
                event.preventDefault();
            }
        };
        window.addEventListener('keydown', listener);
        return () => {
            window.removeEventListener('keydown', listener);
        };
    }, []);
    const searchWidgets = React.useMemo(() => search
        ? widgets.filter((widget) => {
            const label = widgetLabelProvider ? widgetLabelProvider(widget.type) : widget.type;
            return label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
        })
        : [], [widgets, widgetLabelProvider, search]);
    return (React.createElement(Box, { sx: { height: '100%', overflowX: 'hidden', overflowY: 'auto', scrollbarGutter: 'stable', userSelect: 'none' } },
        groups.map((group) => (React.createElement(Accordion, { key: group.name, square: true, expanded: expandedGroupName === group.name, onChange: handleExpandedGroupChange(group.name) },
            React.createElement(AccordionSummary, null,
                React.createElement(Typography, null, groupLabelProvider ? groupLabelProvider(group.name) : group.name)),
            React.createElement(AccordionDetails, null, group.widgets.map((widget) => {
                var _a;
                return (React.createElement(ToolbarButton, { key: widget.type, label: widgetLabelProvider ? widgetLabelProvider(widget.type) : widget.type, icon: widget.icon, createWidget: (_a = widget.createWidget) !== null && _a !== void 0 ? _a : noop }));
            }))))),
        React.createElement(Accordion, { square: true, expanded: expandedGroupName === searchAllComponentsGroupName, onChange: handleExpandedGroupChange(searchAllComponentsGroupName) },
            React.createElement(AccordionSummary, null,
                React.createElement(Typography, null, "Search All Components")),
            React.createElement(AccordionDetails, null,
                React.createElement(TextField, { inputRef: searchInputRef, sx: { gridColumn: '1 / 3', bgcolor: 'background.paper' }, size: 'small', value: search, onChange: (event) => setSearch(event.target.value) }),
                search &&
                    searchWidgets
                        .filter((widget, index) => index < maxSearchResultLength)
                        .map((widget) => {
                        var _a;
                        return (React.createElement(ToolbarButton, { key: widget.type, label: widgetLabelProvider ? widgetLabelProvider(widget.type) : widget.type, icon: widget.icon, createWidget: (_a = widget.createWidget) !== null && _a !== void 0 ? _a : noop }));
                    }),
                search && searchWidgets.length > maxSearchResultLength && (React.createElement(Typography, { color: 'textSecondary', variant: 'body2', align: 'center', sx: { gridColumn: '1 / 3' } },
                    "...and ",
                    searchWidgets.length - maxSearchResultLength,
                    " component",
                    searchWidgets.length > maxSearchResultLength + 1 ? 's' : '')),
                !search && (React.createElement(Typography, { color: 'textSecondary', variant: 'body2', align: 'center', sx: { gridColumn: '1 / 3' } }, "type component name..."))))));
};
const maxSearchResultLength = 10;
const searchAllComponentsGroupName = 'internal-search-all-components';
function splitWidgetsIntoGroups(widgets) {
    const groups = {};
    for (const widget of widgets) {
        if (widget.createWidget) {
            const groupName = getGroupName(widget);
            let group = groups[groupName];
            if (!group) {
                group = { name: groupName, widgets: [] };
                groups[groupName] = group;
            }
            group.widgets.push(widget);
        }
    }
    return Object.values(groups);
}
function getGroupName(widget) {
    const i = widget.type.indexOf('.');
    if (i !== -1) {
        return widget.type.substring(0, i);
    }
    else {
        return 'other';
    }
}
function noop() {
    // do nothing
}
